import React from 'react';
import { Link } from 'react-router-dom';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PlayerStatsCard = ({ player, onCompare, onImageClick, onNameClick, onTeamClick, isSelected, showCompare = true }) => {
  const {
    id,
    name,
    photo,
    photoAlt,
    team,
    position,
    jerseyNumber,
    stats,
    achievements
  } = player;

  const teamName = typeof team === 'string' ? team : team?.name || 'No Team';

  const statItems = [
    { label: 'PPG', value: stats?.pointsPerGame, icon: 'Target' },
    { label: 'RPG', value: stats?.reboundsPerGame, icon: 'RotateCcw' },
    { label: 'APG', value: stats?.assistsPerGame, icon: 'Users' },
    { label: 'FG%', value: `${stats?.fieldGoalPercentage ?? 0}%`, icon: 'Percent' }
  ];

  return (
    <div className={`bg-card rounded-lg border ${isSelected ? 'border-primary' : 'border-border'} p-6 card-shadow transition-all micro-interaction hover:shadow-lg`}>
      {/* Player Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-4 min-w-0">
          <div className="relative shrink-0">
            <div 
              className="w-16 h-16 rounded-full overflow-hidden bg-muted cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => onImageClick && onImageClick(photo, photoAlt || `Photo of ${name}`)}
            >
              <Image
                src={photo}
                alt={photoAlt}
                className="w-full h-full object-cover"
              />
            </div>
            {jerseyNumber != null && jerseyNumber !== '' && (
              <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-primary rounded-full flex items-center justify-center text-xs font-bold text-primary-foreground">
                {jerseyNumber}
              </div>
            )}
          </div>
          
          <div className="min-w-0">
            {onNameClick ? (
              <button
                onClick={() => onNameClick(player)}
                className="text-lg font-semibold text-foreground hover:text-primary transition-colors text-left"
              >
                {name}
              </button>
            ) : (
              <Link 
                to={`/browse-players/${id}`}
                className="text-lg font-semibold text-foreground hover:text-primary transition-colors"
              >
                {name}
              </Link>
            )}
            <p className="text-sm text-muted-foreground">
              {position} •{' '}
              {onTeamClick && teamName !== 'No Team' ? (
                <button
                  type="button"
                  onClick={() => onTeamClick(player)}
                  className="hover:text-primary hover:underline transition-colors"
                >
                  {teamName}
                </button>
              ) : (
                teamName
              )}
            </p>
            <div className="flex items-center space-x-2 mt-1">
              {achievements?.map((achievement, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary"
                >
                  <Icon name="Award" size={12} className="mr-1" />
                  {achievement}
                </span>
              ))}
            </div>
          </div>
        </div>

        {showCompare && onCompare && (
          <Button
            variant={isSelected ? "default" : "outline"}
            size="sm"
            onClick={() => onCompare(player)}
            iconName={isSelected ? "Check" : "Plus"}
            iconPosition="left"
            className="shrink-0"
          >
            {isSelected ? 'Selected' : 'Compare'}
          </Button>
        )}
      </div>
      {/* Statistics Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statItems?.map((stat, index) => (
          <div key={index} className="text-center p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center justify-center mb-2">
              <Icon name={stat?.icon} size={16} className="text-primary" />
            </div>
            <p className="text-lg font-bold text-foreground">{stat?.value ?? 0}</p>
            <p className="text-xs text-muted-foreground">{stat?.label}</p>
          </div>
        ))}
      </div>
      {/* Performance Indicators */}
      <div className="mt-4 pt-4 border-t border-border">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Efficiency Rating</span>
          <div className="flex items-center space-x-2">
            <div className="w-20 h-2 bg-muted rounded-full overflow-hidden">
              <div 
                className="h-full bg-primary rounded-full transition-all"
                style={{ width: `${Math.min(stats?.efficiency || 0, 100)}%` }}
              />
            </div>
            <span className="font-medium text-foreground">{stats?.efficiency ?? 0}</span>
          </div>
        </div>
        
        <div className="flex items-center justify-between text-sm mt-2">
          <span className="text-muted-foreground">Games Played</span>
          <span className="font-medium text-foreground">{stats?.gamesPlayed ?? 0}</span>
        </div>
      </div>
    </div>
  );
};

export default PlayerStatsCard;